// src/services/pdfAnalysisService.ts

import { Section } from '../types';
import { tt, Language } from '../i18n';

interface PDFAnalysisResult {
  sections: Section[];
  suggestedSurgeryName: string;
}

interface GPTSection {
  title: string;
  content: string;
  order: number;
}

interface GPTResponse {
  surgeryName: string;
  sections: GPTSection[];
}

// PDF 텍스트 추출 함수
export const extractTextFromPDF = async (file: File): Promise<string> => {
  // 정적 import로 변경
  const pdfjsLib = await import('pdfjs-dist');
  
  // Worker 설정 - import된 객체에서 직접 접근
  if (typeof window !== 'undefined' && pdfjsLib.GlobalWorkerOptions) {
    pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js`;
  }
  
  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
  
  let fullText = '';
  
  for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
    const page = await pdf.getPage(pageNum);
    const textContent = await page.getTextContent();
    const pageText = textContent.items
      .map((item: any) => item.str)
      .join(' ');
    fullText += pageText + '\n\n';
  }
  
  return fullText;
};

// 텍스트 길이 제한 함수 (토큰 수 대략 계산)
const limitTextLength = (text: string, maxTokens: number = 8000): string => {
  // 대략적으로 1 토큰 = 4 글자로 계산
  const maxChars = maxTokens * 4;
  if (text.length <= maxChars) {
    return text;
  }
  
  // 텍스트를 앞부분과 뒷부분으로 나누어서 중요한 부분 유지
  const frontPart = text.substring(0, maxChars * 0.7);
  const backPart = text.substring(text.length - maxChars * 0.3);
  
  return frontPart + '\n\n... [중간 내용 생략] ...\n\n' + backPart;
};

// Gemini API 호출 함수 (PDF 없이도 동작하도록 수정)
const GEMINI_MODEL = 'gemini-2.5-flash';

export const analyzePDFWithGPT = async (
  pdfTexts: string[],
  language: Language,
  customInstructions?: string
): Promise<PDFAnalysisResult> => {
  const apiKey = process.env.REACT_APP_GOOGLE_API_KEY;
  if (!apiKey) {
    throw new Error(tt('services.pdf.api_key_not_set', undefined, language));
  }

  let systemPrompt: string;
  let userPrompt: string;

  // 시스템 프롬프트는 항상 동일
  systemPrompt = tt('prompts.pdfAnalysis.system', { customInstructions }, language);

  if (pdfTexts.length > 0) {
    // PDF가 있는 경우
    const limitedTexts = pdfTexts.map(text => limitTextLength(text));
    const combinedText = limitedTexts.join('\n\n--- 다음 PDF 파일 ---\n\n');

    userPrompt = tt('prompts.pdfAnalysis.user', {
      pdfContent: combinedText,
      customInstructions
    }, language);
  } else {
    // PDF가 없는 경우 - pdfContent를 null로 전달하여 조건부 렌더링 활용
    userPrompt = tt('prompts.pdfAnalysis.user', {
      pdfContent: null,
      customInstructions
    }, language);
  }

  try {
    const requestBody = {
      systemInstruction: {
        parts: [{ text: systemPrompt }]
      },
      contents: [
        {
          role: 'user',
          parts: [{ text: userPrompt }]
        }
      ],
      generationConfig: {
        temperature: 0.3,
        maxOutputTokens: 4000,
        responseMimeType: 'application/json'
      }
    };

    const response = await fetch(
      `https://generativelanguage.googleapis.com/v1beta/models/${GEMINI_MODEL}:generateContent?key=${apiKey}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(tt('services.pdf.api_error', { status: response.status, text: errorText }, language));
    }

    const data = await response.json();
    const content = data.candidates?.[0]?.content?.parts?.[0]?.text;

    if (!content) {
      throw new Error(tt('services.pdf.invalid_response_format', undefined, language));
    }

    let gptResponse: GPTResponse;
    try {
      gptResponse = JSON.parse(content);
    } catch (parseError) {
      throw new Error(tt('services.pdf.json_parse_error', undefined, language));
    }

    if (!gptResponse.surgeryName || !gptResponse.sections || !Array.isArray(gptResponse.sections)) {
      throw new Error(tt('services.pdf.invalid_response_format', undefined, language));
    }

    const sections: Section[] = gptResponse.sections
      .sort((a, b) => a.order - b.order)
      .map((section, index) => ({
        id: (index + 1).toString(),
        title: section.title,
        content: section.content,
        attachments: []
      }));

    return { sections, suggestedSurgeryName: gptResponse.surgeryName };
  } catch (error) {
    console.error('Gemini 분석 오류:', error);
    if (error instanceof Error) {
      throw error;
    } else {
      throw new Error(tt('services.pdf.unknown_analysis_error', undefined, language));
    }
  }
};

// 메인 PDF 분석 함수 (PDF 없이도 동작하도록 수정)
export const analyzePDFFiles = async (
  files: File[],
  language: Language,
  customInstructions?: string
): Promise<PDFAnalysisResult> => {
  try {
    const pdfTexts: string[] = [];
    
    // PDF 파일이 있는 경우에만 텍스트 추출
    if (files.length > 0) {
      for (const file of files) {
        if (file.type === 'application/pdf') {
          const text = await extractTextFromPDF(file);
          pdfTexts.push(text);
        } else {
          throw new Error(tt('services.pdf.unsupported_file_type', { type: file.type }, language));
        }
      }
    }
    
    // PDF가 없어도 GPT 분석 실행 (빈 배열 전달)
    return await analyzePDFWithGPT(pdfTexts, language, customInstructions);
  } catch (error) {
    console.error('PDF 분석 오류:', error);
    throw error;
  }
};

// API 키 검증 함수
export const validateAPIKey = async (): Promise<boolean> => {
  const apiKey = process.env.REACT_APP_GOOGLE_API_KEY;

  if (!apiKey) {
    return false;
  }

  try {
    const response = await fetch(
      `https://generativelanguage.googleapis.com/v1beta/models?key=${apiKey}`
    );
    return response.ok;
  } catch {
    return false;
  }
};